<template lang="pug">
    section(v-if="activeCms")
        .category-page.not-found(v-if="notFound")
            Error(:message="$t('Category Page Not Found')")
        .category-page.not-found(v-else-if="pathNotValid")
            Error(:message="$t('URL Not Valid')")
        .category-page.not-found(v-else-if="error")
            Error(:message="$t('Something went wrong!')" :code="501")
        .category-page(v-else)
            tabs(:items="tabs" :active-tab="$route.fullPath" @click="activateTab")
                template(slot="tabbar-buttons")
                    // el-tooltip(class="item" effect="dark" :content="$t('Delete')" placement="bottom")
                    //     el-button.delete-button(circle type="danger" icon="el-icon-fa-trash-alt far")
                    // el-tooltip(class="item" effect="dark" :content="$t('Dismiss')" placement="bottom")
                    //     el-button(circle icon="el-icon-fa-times fas")
                    // el-tooltip(class="item" effect="dark" :content="$t('Save and close')" placement="bottom")
                    //     el-button(circle type="primary" icon="el-icon-fa-check fas")
                    // el-tooltip(class="item" effect="dark" :content="$t('Save')" placement="bottom")
                    //     el-button(circle type="success" icon="el-icon-fa-save fas")
                    el-tooltip(v-if="! loading && isDeletable" class="item" effect="dark" :content="$t('Delete')" placement="top")
                        el-button.delete-button(
                            circle
                            type="danger"
                            icon="el-icon-fa-trash-alt far"
                            :loading="deleting"
                            :disabled="isDisabledSubmit && ! deleting"
                            @click="deleteCurrentCategoryPage"
                        )
                    el-tooltip(class="item" effect="dark" :content="$t('Dismiss')" placement="top")
                        el-button(
                            circle icon="el-icon-fa-times fas"
                            :disabled="isDisabledSubmit"
                            @click="leave"
                        )
                    el-tooltip(v-if="! loading" class="item" effect="dark" :content="$t('Save and close')" placement="top")
                        el-button(
                            circle
                            type="primary"
                            icon="el-icon-fa-check fas"
                            :disabled="isDisabledSubmit && ! savingAndClose"
                            :loading="savingAndClose"
                            @click="submit($event, listRoute)"
                        )
                    el-tooltip(v-if="! loading" class="item" effect="dark" :content="$t('Save')" placement="top")
                        el-button(
                            circle
                            type="success"
                            icon="el-icon-fa-save fas"
                            :disabled="isDisabledSubmit && ! saving"
                            :loading="saving"
                            @click="submit"
                        )

            el-form(label-position="top" :model="form" :rules="rules" ref="form")
                all-input-fields
                el-card(shadow="always" v-loading="loading")
                    router-view.category-page-form(:key="$route.name" :class="{'loading-panel': loading}")
                .buttons-flex
                    .form-buttons
                        el-button.preview-button(
                            round plain
                            v-if="form && form.id && form.key"
                            type="link"
                            icon="el-icon-fa-eye far"
                            @click="showPreviewModal"
                        )
                            | {{ $t('Preview') }}
                            kbd.shortcut-hint {{ previewShortcut }}
                    .form-buttons
                        el-button(
                            round v-if="! loading"
                            icon="el-icon-fa-times fas"
                            :disabled="isDisabledSubmit"
                            @click="leave"
                        ) {{ $t('Dismiss') }}
                        el-button(
                            round v-if="! loading"
                            type="primary"
                            icon="el-icon-fa-check fas"
                            :loading="savingAndClose"
                            :disabled="isDisabledSubmit && ! savingAndClose"
                            @click="submit($event, listRoute)"
                        ) {{ $t('Save and close') }}
                        el-button(
                            round v-if="! loading"
                            type="success"
                            icon="el-icon-fa-save fas"
                            :loading="saving"
                            :disabled="isDisabledSubmit && ! saving"
                            @click="submit"
                        ) {{ $t('Save') }}

        PreviewDrawer(
            :visible.sync="previewDrawer.visibility"
            :src="previewDrawer.src"
            :loading="previewDrawer.loading"
            @closed="resetPreviewModal"
        )
</template>

<i18n>
{
    "de": {
        "Content": "Inhalt",
        "Settings": "Einstellungen",
        "Teaser could not be saved.": "Anreißer konnte nicht gespeichert werden.",
        "Dependencies could not be fetched.": "Abhängigkeiten konnten nicht abgerufen werden.",
        "Category Page could not be fetched.": "Kategorieseite konnte nicht gefunden werden.",
        "Category Page could not be deleted.": "Kategorieseite konnte nicht gelöscht werden.",
        "Category Page deleted successfully!": "Kategorieseite erfolgreich gelöscht!",
        "Category Page could not be saved.": "Kategorieseite konnte nicht gespeichert werden.",
        "Category Page saved successfully!": "Kategorieseite erfolgreich gespeichert!",
        "Category Page Not Found": "Kategorieseite nicht gefunden"
    }
}
</i18n>

<script>
import { isApplePlatform } from '@/helpers/shortcuts';
import _ from 'lodash';
import pinia from '@/pinia';
import { useSetupStore, usePageStore } from '@/stores';

import store from '@/store';
import router from '@/router';

import { checkRestrictions } from '@/helpers';
import Tabs from '@/components/Tabs';
import Error from '@/components/Error';
import PreviewDrawer from '@/components/PreviewDrawer';

// validate all fields, regardless if the corresponding component is mounted or not
import AllInputFields from './AllInputFields';

export default {
    name: 'CategoryPage',
    data() {
        return {
            error: false,
            notFound: false,
            pathNotValid: false,
            loading: false,
            saving: false,
            savingAndClose: false,
            deleting: false,
            previewDrawer: {
                visibility: false,
                src: undefined,
                loading: false
            }
        };
    },
    components: {
        Tabs,
        Error,
        AllInputFields,
        PreviewDrawer
    },
    watch: {
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true);
            if (reload) {
                this.pageStore.setFormInitialized(false);
                this.initialize();
            }
        }
    },
    computed: {
        id() {
            return _.get(this.form, 'id', false);
        },
        isDeletable() {
            return !!this.id;
        },
        isDisabledSubmit() {
            return this.saving || this.savingAndClose || this.deleting;
        },
        key() {
            // const route = router.history.current.matched.find(route => route.components.default.name === 'CategoryPage')
            // const key = _.get(route, 'meta.key')
            //
            // return key

            return this.$route.params.key;
        },
        listRoute() {
            let route = { name: 'dashboard' };
            if (this.key) route = { name: 'posts', params: { key: this.key } };
            return route;
        },
        // activeTab() {
        //     // return _.replace(store.getters.activeTabKey, ':id', this.$route.params.id)
        //
        //     return this.$route.fullPath
        // },
        tabs() {
            // let tabs =  _.cloneDeep(store.getters.currentTabs)
            // _.map(tabs, tab => {
            //     tab.path = _.replace(tab.path, ':id', this.$route.params.id)
            // })
            // return tabs

            const key = this.$route.params.key;
            const id = this.$route.params.id;

            let tabs = [
                {
                    name: this.$t('Content'),
                    path: `/post/${key}/${id}/main`,
                    icon: {
                        name: 'newspaper',
                        group: 'far'
                    }
                },
                {
                    name: this.$t('Meta'),
                    path: `/post/${key}/${id}/meta`,
                    icon: {
                        name: 'info',
                        group: 'fas'
                    }
                },
                {
                    name: this.$t('Settings'),
                    path: `/post/${key}/${id}/settings`,
                    icon: {
                        name: 'cog',
                        group: 'fas'
                    }
                },
            ];

            if (this.options.filter.items.length) {
                tabs.push({
                    name: this.$t('Filter'),
                    path: `/post/${key}/${id}/filters`,
                    icon: {
                        name: 'filter',
                        group: 'fas'
                    }
                })
            }

            return tabs;
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        enabledExtensions() {
            return useSetupStore().enabledExtensions;
        },
        pageStore() {
            return usePageStore();
        },
        form() {
            return this.pageStore.form;
        },
        options() {
            return this.pageStore.options;
        },
        initialized() {
            return this.pageStore.formInitialized;
        },
        validateOnEnter() {
            return this.pageStore.formValidateOnEnter;
        },
        rules() {
            return this.pageStore.rules;
        },
        previewShortcut() {
            return isApplePlatform() ? '⌘P' : 'Ctrl+P';
        }
    },
    methods: {
        initialize() {
            if (this.initialized !== true) {
                this.pageStore.setFormInitialized(true);

                let dependencies = []
                const fetchDependencies = () => {
                    dependencies = [
                        this.pageStore.fetchCategoryOptions(this.activeCms.id),
                        this.pageStore.fetchMenuOptions(this.activeCms.id),
                        this.pageStore.fetchTagOptions(this.activeCms.id),
                        this.pageStore.fetchFileTagOptions(this.activeCms.id),
                    ];

                    // extensions
                    // fetch only if the corresponding extension is availible / active

                    if (this.enabledExtensions.includes('event-manager') && checkRestrictions('ex:evnt')) {
                        dependencies.push(this.pageStore.fetchEventManagerCategoryOptions(this.activeCms.id));
                    }
                    if (this.enabledExtensions.includes('shop') && checkRestrictions('ex:shop')) {
                        dependencies.push(this.pageStore.fetchShopCategoryOptions(this.activeCms.shop.id));
                        dependencies.push(this.pageStore.fetchShopPropertyOptions(this.activeCms.shop.id));
                        dependencies.push(this.pageStore.fetchShopTagOptions(this.activeCms.shop.id));
                    }

                    return Promise.allSettled(dependencies);
                };

                this.error = false;
                this.loading = true;

                if (this.$route.params.id === 'new') {
                    this.pageStore.reset();
                    this.pageStore.initializeTeaser();

                    fetchDependencies()
                        .then(function (results) {
                            this.pageStore.initializeCategoryPage(this.key);
                            this.loading = false;

                            var rejected = results.filter(function (result) {
                                return result.status === 'rejected';
                            });
                            if (rejected.length) {
                                rejected.forEach(function (result) {
                                    // eslint-disable-next-line no-console
                                    console.warn('Dependency fetch failed:', result.reason);
                                });
                            }
                        }.bind(this));
                } else if (!isNaN(this.$route.params.id)) {
                    this.pageStore.fetchCategoryPage(this.$route.params.id)
                        .then(() => {
                            // this.pageStore.resetUI()

                            this.loading = false;
                            this.notFound = false;

                            if (this.form.category) {
                                dependencies.push(this.pageStore.fetchFilterOptions({
                                    cms: this.activeCms.id,
                                    category: this.form.category.id,
                                }));
                            }

                            // fetch options for dropdowns
                            fetchDependencies()
                                .then(function (results) {
                                    this.pageStore.resetRules();
                                    // this.pageStore.resetUI()

                                    var rejected = results.filter(function (result) {
                                        return result.status === 'rejected';
                                    });
                                    if (rejected.length) {
                                        rejected.forEach(function (result) {
                                            // eslint-disable-next-line no-console
                                            console.warn('Dependency fetch failed:', result.reason);
                                        });
                                    }
                                }.bind(this));
                        })
                        .catch(err => {
                            this.loading = true;
                            this.notFound = true;

                            /* eslint no-console: ["error", { allow: ["error"] }] */
                            console.error(err);

                            this.$notify.error({
                                title: this.$t('An error occurred!'),
                                message: this.$t('Category Page could not be fetched.'),
                                iconClass: 'fas el-icon-fa-xmark',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000
                            });
                        });
                } else {
                    this.loading = true;
                    this.pathNotValid = true;
                }
            } else {
                if (this.validateOnEnter === true) {
                    setTimeout(() => {
                        this.$refs.form.validate(() => {
                            // do nothing
                            // callback function is required :(
                        });
                    }, 100);
                }
            }
        },
        deleteCurrentCategoryPage() {
            this.deleting = true;
            this.pageStore.delete(this.id)
                .then(() => {
                    // this.deleting = false

                    router.push(this.listRoute);

                    this.$notify.success({
                        title: this.$t('Category Page deleted successfully!'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                })
                .catch(err => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);

                    this.deleting = false;

                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Category Page could not be deleted.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                });
        },
        async submit(event, redirect = false) {
            this.$refs.form.validate((valid, errors) => {
                if (valid) {
                    if (redirect) this.savingAndClose = true;
                    else this.saving = true;

                    const payload = _.cloneDeep(this.form);

                    if (!this.id) {
                        // NOTE: save everything
                        // payload.sections = payload.sections.filter(el => el.key === 'category-page-teaser')

                        // INFO: deconstruct grids
                        // const deconstructGrid = grid => {
                        //     if (!grid.gridAreas || !grid.gridAreas.length) return

                        //     const section = _.cloneDeep(grid)
                        //     section.gridAreas.forEach(area => delete area.gridItems)
                        //     payload.sections.push(section);

                        //     grid.gridAreas.forEach(area => {
                        //         if (area.gridItems && area.gridItems.length) {
                        //             area.gridItems.forEach(section => {
                        //                 // payload.sections.push({...section, gridArea: { id: area.id }});
                        //                 section.gridArea = { id: area.id };
                        //             });
                        //             walkSections(area.gridItems)
                        //         }
                        //     })
                        // }

                        // const walkSections = sections => {
                        //     sections.sort((a, b) => _.get(a, 'position', 0) - _.get(b, 'position', 0)).forEach(section => {
                        //         if (section.type === 'grid') {
                        //             deconstructGrid(section)
                        //         } else {
                        //             payload.sections.push(section);
                        //         }
                        //     })
                        // }

                        // const sections = _.cloneDeep(payload.sections)
                        // payload.sections = []

                        // walkSections(sections)

                    } else {
                        const teaser = payload.sections.find(el => el.key === 'category-page-teaser')

                        const otherTeasers = payload.sections.filter(el => el.key === 'category-page-teaser' && el.id !== teaser.id)
                        otherTeasers.forEach(teaser => this.pageStore.deleteSection({ section: teaser }))

                        delete payload.sections;

                        this.pageStore.saveSection({ section: teaser })
                            .then(newSection => {
                                this.pageStore.replaceSection({ oldSection: teaser, newSection })
                            })
                            .catch(error => {
                                this.$notify.error({
                                    title: this.$t('An error occurred!'),
                                    message: this.$t('Teaser could not be saved.'),
                                    iconClass: 'fas el-icon-fa-xmark',
                                    position: 'bottom-left',
                                    customClass: 'dark',
                                    duration: 8000
                                });

                                // this.$nextTick(() => {
                                //     store.commit('page/sortSections', {
                                //         path: this.path,
                                //         sections: this.sections,
                                //     })
                                // })

                                console.error(error)
                            })
                    }

                    // wait for tag timeout - workaround
                    // TODO: find a better solution
                    setTimeout(async () => {
                        await this.pageStore.extendedSave(payload)
                            .then(page => {
                                this.$notify.success({
                                    title: this.$t('Category Page saved successfully!'),
                                    iconClass: 'fas el-icon-fa-check',
                                    position: 'bottom-left',
                                    customClass: 'dark',
                                    duration: 8000
                                });

                                if (redirect) {
                                    this.savingAndClose = false;
                                    setTimeout(() => router.push(redirect), 100)
                                } else {
                                    if (this.$route.params.id === 'new') {
                                        const route = router.currentRoute
                                        route.params.id = page.id
                                        router.replace(route)
                                    }

                                    this.pageStore.insertPage(_.cloneDeep(page));
                                    this.$nextTick(() => this.pageStore.initializeTeaser());
                                    setTimeout(() => {
                                        this.pageStore.resetRules()
                                        this.saving = false;
                                    }, 100);
                                }
                            })
                            .catch(err => {
                                /* eslint no-console: ["error", { allow: ["error"] }] */
                                console.error(err);

                                if (redirect) this.savingAndClose = false;
                                else this.saving = false;

                                this.$notify.error({
                                    title: this.$t('An error occurred!'),
                                    message: this.$t('Category Page could not be saved.'),
                                    iconClass: 'fas el-icon-fa-xmark',
                                    position: 'bottom-left',
                                    customClass: 'dark',
                                    duration: 8000
                                });
                            });
                    }, 151);
                } else {
                    this.pageStore.setFormValidateOnEnter(true);

                    // check the invalid fields
                    // and go to the corresponding tab
                    // or open the corresponding closed area
                    // TODO: scroll to invalid field

                    let invalidTabs = [];
                    _.each(errors, (error, path) => {
                        if (_.indexOf(['title', 'key'], path) >= 0) {
                            invalidTabs.push(`/post/${this.key}/${this.$route.params.id}/main`);
                        }
                        if (_.indexOf(['created_at'], path) >= 0) {
                            invalidTabs.push(`/post/${this.key}/${this.$route.params.id}/settings`);
                        }
                    });

                    invalidTabs = _.uniq(invalidTabs);

                    // check if the current tab
                    // is present in invalidTabs
                    // if not, go to next (first) invalid tab

                    const match = _.indexOf(invalidTabs, this.$route.fullPath);

                    let switchTab = false;
                    if (match < 0) {
                        invalidTabs.every(invalidTab => {
                            if (invalidTab && this.activeTab !== invalidTab) {
                                const tab = _.find(this.tabs, { path: invalidTab });
                                if (tab) {
                                    this.activateTab(tab);

                                    // break loop after the first tab has been activated
                                    return false;
                                }
                            }
                        });
                    }

                    // // triger the validation again
                    // // so the previously hidden fields will get the errors
                    // // ---
                    // // does not work if the component has been destroyed
                    // // in the meantime (f.a. by switching tabs)
                    // if (switchTab === false) {
                    //     const form = this.$refs.form
                    //     setTimeout(() => {
                    //         form.validate(() => {
                    //             // do nothing
                    //             // callback function is required :(
                    //         })
                    //     }, 1000);
                    // }

                    this.$notify.warning({
                        title: this.$t('Form is not valid!'),
                        message: this.$t('Correct your input and submit again.'),
                        iconClass: 'fas el-icon-fa-exclamation-circle',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                }
            });
        },
        leave() {
            router.push(this.listRoute);
        },
        activateTab(item) {
            return useSetupStore().activateTab(item);
        },
        showPreviewModal() {
            this.previewDrawer.visibility = true;
            this.previewDrawer.loading = true;

            this.$nextTick(() => {
                const baseUrl = _.get(this.activeCms, 'domains.0.base_url');
                const categoryKey = this.key;
                const slug = _.get(this, 'form.key', '');

                let protocol = 'http:';
                if (process.env.NODE_ENV === 'production') protocol = window.location.protocol;

                let iframeSrc = `${protocol}//${baseUrl}/${categoryKey}/${slug}`;

                const urlObj = new URL(iframeSrc);
                urlObj.searchParams.set('no-cache', 'true');
                this.previewDrawer.src = urlObj.toString();

                this.previewDrawer.loading = false;
            });
        },
        resetPreviewModal() {
            this.previewDrawer = {
                visibility: false,
                src: undefined,
                loading: false
            };
        },
        handleKeyDown(event) {
            // Cmd+P (Mac) or Ctrl+P (Windows/Linux)
            if ((event.metaKey || event.ctrlKey) && event.key === 'p') {
                event.preventDefault();
                if (this.form && this.form.id && this.form.key) {
                    this.showPreviewModal();
                }
            }
        }
    },
    mounted() {
        this.initialize();
        document.addEventListener('keydown', this.handleKeyDown);
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.handleKeyDown);
    },
    beforeRouteEnter(to, from, next) {
        let cms = useSetupStore(pinia).activeCms;
        const categories = _.get(cms, 'config.template.categories') || [];
        const keys = _.map(categories, 'key');

        if (!keys.length) next({ name: 'dashboard' });
        else {
            // redirect if necessary
            let key = _.get(to, 'params.key');
            const id = _.get(to, 'params.id') || 'new';

            if (!key || key === '#' || !keys.includes(key)) {
                key = keys[0];
                next({ name: 'post-main', params: { key, id } });
            } else if (to.name === 'post-form') {
                next({ name: 'post-main', params: { key, id } });
            } else {
                next();
            }
        }
    },
    beforeRouteLeave(to, from, next) {
        next();

        this.pageStore.reset();
        this.pageStore.resetUI();
        this.pageStore.resetRules();
    },
    destroyed() {
        // console.log('destroyed');
    }
};
</script>
